import { useGridApiRef, Table } from '@repo/go-table';
import type { GridColumnResizeParams, GridEventListener } from '@repo/go-table';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'throttle-debounce';

import tableStyles from '../AlertsManagerTable.module.css';
import type { AlertsManagerWidgetState } from '../AlertsManagerWidgetState';
import type { ViewStore } from '../stores/ViewStore';
import { isColumnsOrderModelEqual } from '../utils';

import { ManualAnomaliesTableSettings } from './components/ManualAnomaliesTableSettings';
import { ManualAnomaliesTableToolbar } from './components/ManualAnomaliesTableToolbar';
import { ManualAnomaliesViewActionsBar } from './components/ManualAnomaliesViewActionsBar';
import { MANUAL_ANOMALIES_CONTEXT_COLUMN_FIELDS } from './constants';
import { ManualAnomaliesTableStore } from './ManualAnomaliesTableStore';
import type { ManualAnomaliesService } from './services/ManualAnomaliesService';
import type { ManualAnomaly } from './services/ManualAnomaly.entity';
import { useColumnGroups } from './useColumnGroups';
import { useManualAnomaliesColumns } from './useColumns';

const contextVisibilityOverride = (hidden: boolean): Record<string, boolean> => {
  if (!hidden) return {};
  return Object.fromEntries(MANUAL_ANOMALIES_CONTEXT_COLUMN_FIELDS.map((f) => [f, false]));
};

type Props = {
  viewStore: ViewStore;
  service: ManualAnomaliesService;
  widgetState: AlertsManagerWidgetState;
};

export const ManualAnomaliesTable = observer(function ManualAnomaliesTable({ viewStore, service, widgetState }: Props) {
  const tableApi = useGridApiRef();
  const [wrapperElement, setWrapperElement] = useState<HTMLDivElement | null>(null);

  const store = useMemo(
    () => new ManualAnomaliesTableStore(tableApi, widgetState.notifications, service, widgetState.timeZonesStore),
    [service, tableApi, widgetState.notifications, widgetState.timeZonesStore],
  );

  const columns = useManualAnomaliesColumns(widgetState.timeZonesStore, service, store, widgetState.defaultDepthUnit);
  const columnGroups = useColumnGroups();
  const { displayManager } = store;

  const columnFields = useMemo(() => columns.map((c) => c.field), [columns]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleWidthChange = useCallback(
    debounce(300, ({ colDef: { field }, width }: GridColumnResizeParams) => {
      if (!displayManager) return;
      const cols = tableApi.current.getAllColumns();
      const updated: Record<string, number> = {};
      const columnsWidth = displayManager.getColumnsWidth();
      cols.forEach((column) => {
        if (column.field === field) {
          updated[field] = width;
        } else if (columnsWidth && !columnsWidth[column.field]) {
          updated[column.field] = column.computedWidth;
        }
      });
      displayManager.onWidthChange(updated);
    }),
    [displayManager, tableApi],
  );

  useEffect(() => {
    return store.init();
  }, [store]);

  const { columnsOrderModel, columnsVisibilityModel } = displayManager;

  const handleColumnsOrderModelChange = useCallback(
    (orderModel: string[]) => {
      const isOrderModelValid =
        orderModel.length === columnFields.length && orderModel.every((field) => columnFields.includes(field));

        if (!isOrderModelValid) {
          return;
        }
        
      const hasSavedOrderModel = Boolean(columnsOrderModel?.length);
      const hasCustomSavedOrderModel = hasSavedOrderModel && !isColumnsOrderModelEqual(columnsOrderModel, columnFields);
      const isIncomingDefaultOrderModel = isColumnsOrderModelEqual(orderModel, columnFields);



      if (hasCustomSavedOrderModel && isIncomingDefaultOrderModel) {
        return;
      }

      displayManager.onColumnsOrderModelChange(orderModel);
    },
    [columnFields, columnsOrderModel, displayManager],
  );

  useEffect(() => {
    const columnsWidth = displayManager?.getColumnsWidth();
    if (!displayManager || !columnsWidth) return;
    for (const column of tableApi.current.getAllColumns()) {
      if (columnsWidth[column.field] === column.width) continue;
      tableApi.current.setColumnWidth(column.field, columnsWidth[column.field]);
    }
  }, [tableApi, displayManager]);

  const onClipboardCopy: GridEventListener<'clipboardCopy'> = useCallback(
    (_, event) => {
      event.defaultMuiPrevented = true;
      let timer: number | undefined;

      if (timer != null) {
        return;
      }

      store.handleCopy();

      timer = window.setTimeout(() => {
        timer = undefined;
      }, 300);
    },
    [store],
  );

  const onCellKeydown: GridEventListener<'cellKeyDown'> = useCallback(
    (params, event) => {
      if (event.code === 'KeyV' && (event.metaKey || event.ctrlKey)) {
        event.defaultMuiPrevented = true;
      }

      if (event.code === 'Delete') {
        event.defaultMuiPrevented = true;
        store.handleDelete(params);
      }
    },
    [store],
  );

  useEffect(() => {
    let timer: number | undefined;

    const handlePaste = (event: KeyboardEvent) => {
      if (event.code !== 'KeyV' || (!event.ctrlKey && !event.metaKey)) {
        return;
      }

      // игнорируем, если событие реагирует на вставку текста в input поле
      if (event.target instanceof HTMLInputElement && ['text', 'number', 'date'].includes(event.target.type)) {
        return;
      }

      if (timer != null) {
        return;
      }

      event.preventDefault();
      store.handlePaste();

      timer = window.setTimeout(() => {
        timer = undefined;
      }, 300);
    };

    document.addEventListener('keydown', handlePaste);

    return () => {
      document.removeEventListener('keydown', handlePaste);
      window.clearTimeout(timer);
    };
  }, [store]);

  const mergedVisibility = useMemo(() => {
    return {
      ...columnsVisibilityModel,
      ...contextVisibilityOverride(store.contextColumnsHidden),
    };
  }, [columnsVisibilityModel, store.contextColumnsHidden]);

  const getSelectedRowsIds = useCallback(() => {
    const selectedRows = tableApi.current.getSelectedRows();
    return Array.from(selectedRows.keys()).map((key) => key.toString());
  }, [tableApi]);

  const themeClassName = widgetState.appParams.theme === 'dark' ? 'go-widget-theme-dark' : 'go-widget-theme-light';

  return (
    <div className={clsx('go-widgets-widget-wrapper', themeClassName)} ref={(r) => setWrapperElement(r)}>
      <div className="go-widgets-content" style={{ height: '100%', position: 'relative' }}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '0 16px 16px' }}>
          <ManualAnomaliesTableToolbar
            viewStore={viewStore}
            contextHidden={store.contextColumnsHidden}
            onToggleContext={() => store.toggleContextColumnsHidden()}
            isEditModeActive={store.isEditing}
            onAddRow={() => service.addNewManualAnomalyItems()}
            onDelete={() => service.deleteManualAnomalyItems(getSelectedRowsIds())}
            onChooseRow={() => store.setIsRowSelectionModeOn(!store.isRowSelectionModeOn)}
            deleteDisabled={service.loading || store.selectedRows.length === 0}
          />
          <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
            <Table<ManualAnomaly>
              initialState={{
                sorting: { sortModel: displayManager.sortingModel },
                filter: { filterModel: displayManager.filterModel },
                columns: { columnVisibilityModel: mergedVisibility },
                pinnedColumns: displayManager.pinnedColumns,
              }}
              sortModel={displayManager.sortingModel}
              filterModel={displayManager.filterModel}
              checkboxSelection={store.isRowSelectingEnabled}
              columnVisibilityModel={mergedVisibility}
              columnsOrderModel={displayManager.columnsOrderModel}
              onColumnVisibilityModelChange={displayManager.onColumnVisibilityChange}
              onColumnsOrderModelChange={handleColumnsOrderModelChange}
              onPinnedColumnsChange={displayManager.onPinChange}
              onFilterModelChange={displayManager.onFilterChange}
              onSortModelChange={displayManager.onSortChange}
              onColumnResize={handleWidthChange}
              onRowSelectionModelChange={store.setSelectedRows}
              onClipboardCopy={onClipboardCopy}
              onCellKeyDown={onCellKeydown}
              isCellEditable={(params) => store.isCellEditable(params)}
              getRowId={(row) => row.entityId}
              apiRef={tableApi}
              className={tableStyles.table}
              columns={columns}
              columnGroupingModel={columnGroups}
              rows={service.rows}
              loading={service.loading}
              editMode="cell"
              cellSelection
            />
          </div>
        </div>
      </div>
      <div className="go-widgets-actions-bar-wrapper">
        <ManualAnomaliesViewActionsBar widgetState={widgetState} store={store} service={service} />
      </div>
      <ManualAnomaliesTableSettings
        displayManager={displayManager}
        columns={columns}
        open={store.isTableSettingsOpen}
        onClose={() => store.setIsTableSettingsOpen(false)}
        container={wrapperElement ?? undefined}
      />
    </div>
  );
});
